import { useEffect, useRef } from 'react'

/**
 * Stable key for a params object: object keys sorted at every level, so two
 * objects holding the same values always serialize identically regardless of
 * the order they were built in.
 */
export const paramsKey = (params) => {
  if (params == null) return 'null'
  try {
    return JSON.stringify(params, (_k, v) =>
      v && typeof v === 'object' && !Array.isArray(v)
        ? Object.keys(v).sort().reduce((acc, k) => { acc[k] = v[k]; return acc }, {})
        : v
    )
  } catch {
    // Circular / non-serializable params: fall back to re-running on every
    // change, i.e. the pre-CC-49994 behavior, rather than silently never
    // refreshing. A stale report is worse than a redundant fetch.
    return null
  }
}

/**
 * CC-49835's debounced "re-run whenever a filter changes" auto-run, keyed on the
 * filter VALUES rather than the params object's identity.
 *
 * CC-49994: the financial-statement pages all ran
 *
 *     const params    = useMemo(() => ({ ...filters }), [filters])
 *     const runReport = useCallback(async () => { … }, [params])
 *     useEffect(() => { const t = setTimeout(runReport, 300); … }, [runReport])
 *
 * `{ ...filters }` is a NEW object every time `filters` changes identity, and
 * `setFilters(f => ({ ...f, startDate: v }))` returns a new object even when `v`
 * is identical to what was already there. So any control emitting onChange with
 * an UNCHANGED value produced new `params` → new `runReport` → a full report
 * re-fetch. Clicking into the date picker and clicking away was enough, on every
 * report in this family.
 *
 * Keying on the serialized values makes a no-op change a no-op. A real change
 * still re-runs, the mount run still happens (first load), and the explicit Run
 * button is untouched — it calls `run` directly, so deliberately re-running the
 * same filters still works.
 *
 * @param run    the (async) report runner. Always invoked through a ref, so its
 *               identity changing does NOT schedule a run — only the values do.
 * @param params the filter values that should trigger a re-run when they change.
 *               Include anything outside `params` that the runner reads (e.g.
 *               P&L's `chooseReport`), or that change won't re-run the report.
 * @param delay  debounce in ms (default 300 — keystrokes in a date input).
 */
export default function useAutoRunOnFilterChange(run, params, delay = 300) {
  const runRef = useRef(run)
  runRef.current = run
  const key = paramsKey(params)
  // Unserializable params (key === null): keep the old identity-based trigger so
  // the report still refreshes, just without the no-op suppression.
  const dep = key === null ? params : key
  useEffect(() => {
    const t = setTimeout(() => runRef.current(), delay)
    return () => clearTimeout(t)
  }, [dep, delay])
}
